import React, { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PhotoIcon, TrashIcon, PencilSquareIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { ColumnDef } from '@tanstack/react-table';
import { BasicTable } from '../../components/BasicTable';
import { Asset } from '../../models/Asset';
import { useAssetById } from '../../hooks/UseAsset';
import { useApi } from '../../contexts/ApiContext';
import { useToast } from '../../contexts/ToastContext';
import { Content } from '../../models/Content';
import { DeleteConfirmation } from '../../components/DeleteConfirmation';
import ImageModal from '../Asset/ImageModal';
import { ImageDisplay } from '../../models/Image';
import { useAuth } from '../../contexts/AuthContext';
import { Role } from '../../models/Role';
import { isRoleHighEnough } from '../../Helper';

export const ContentList: React.FC<{}> = () => {
  const { roles } = useAuth();
  const { id = '' } = useParams<{ id: string }>();
  const api = useApi();
  const { addToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [imagesDisplay, setImagesDisplay] = useState<ImageDisplay[]>([]);
  const { data: { contents } = {} as Asset, refetch } = useAssetById(parseInt(id));
  const [contentsArray, setContentsArray] = useState<Content[]>([]);
  const [confirmationBox, setConfirmationBox] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<boolean>(false);
  const [contentsId, setContentsId] = useState<number>(0);

  useEffect(() => {
    if (contents !== undefined && contents.length > 0) {
      setContentsArray(contents);
    }
  }, [contents]);

  const {
    formState: { errors },
  } = useForm<Comment>({
    reValidateMode: 'onBlur',
  });

  const openConfirmationBox = (nid: number): void => {
    setContentsId(nid);
    setConfirmationBox(true);
  };

  const openEditModal = (nid: number): void => {
    setContentsId(nid);
    setEditModal(true);
  };

  const removeContents = (): void => {
    api.post('/api/Asset/DeleteContents', { id: contentsId }).then(
      (result) => {
        result && refetch();
        addToast('Deleted Contents');
      },
      (error) => {
        addToast('Unable to delete Contents');
      }
    );
  };
  console.log('contents', contents);

  const columns = React.useMemo<ColumnDef<Content>[]>(
    () => [
      {
        header: 'Item',
        accessorKey: 'item',
        meta: {
          className: 'px-2 py-3 whitespace-nowrap text-sm leading-5 font-medium',
        },
      },
      {
        header: 'Type',
        accessorKey: 'type',
        meta: {
          className: 'px-2 py-3 whitespace-nowrap text-sm leading-5',
        },
      },
      {
        header: 'Rate',
        accessorKey: 'appliedRate',
        meta: {
          headerClassName: 'text-right whitespace-normal',
          className: 'px-2 py-3 whitespace-nowrap text-sm leading-5 text-right',
        },
      },
      {
        header: 'Quantity',
        accessorKey: 'quantity',
        meta: {
          headerClassName: 'text-right whitespace-normal',
          className: 'px-2 py-3 whitespace-nowrap text-sm leading-5 text-right',
        },
      },
      {
        header: 'Total',
        accessorKey: '',
        meta: {
          headerClassName: 'text-right whitespace-normal',
          className: 'px-2 py-3 whitespace-nowrap text-sm leading-5 text-right',
        },
        cell: ({ row }): JSX.Element => <div>{(row.original.quantity || 1) * (row.original.rate || 1)}</div>,
      },
      {
        header: (): React.ReactElement => (
          <Link to={`/contents/${0}/edit/${id}`} className="inline-flex items-center">
            <PlusIcon className="mr-5 h-4 w-4 text-indigo-600" />
          </Link>
        ),
        accessorKey: 'id',
        headerClassName: 'text-right whitespace-normal',
        className: 'px-2 py-3 whitespace-nowrap text-sm leading-5',
        cell: ({ row }): React.ReactElement => (
          <div className="flex flex-row justify-end space-x-4">
            <button
              onClick={(): void => {
                setIsModalOpen(true);
                setImagesDisplay(row.original?.images || []);
              }}
              disabled={row.original?.images?.length == 0}
            >
              <PhotoIcon
                className={`h-4 w-4 ${
                  row.original?.images?.length == 0 ? 'text-gray-300' : 'text-indigo-600'
                }  font-medium rounded-md focus:outline-none`}
              />
            </button>
            <Link to={`/contents/${row.original.id}/edit`} className="btn-link">
              <PencilSquareIcon className="text-indigo-600 h-4 w-4" />
            </Link>
            {isRoleHighEnough(roles, Role.Editor) && (
              <button onClick={(): void => openConfirmationBox(row.original.id)}>
                <TrashIcon className="h-4 w-4 text-indigo-600  font-medium rounded-md hover:text-red-700 focus:outline-none" />
              </button>
            )}
          </div>
        ),
      },
    ],
    []
  );

  return (
    <>
      <div className="m-3">
        <BasicTable columns={columns} data={contents || []} isLoading={contents === undefined} tablePageSize={10} showHead />
      </div>
      {imagesDisplay && imagesDisplay.length > 0 && (
        <ImageModal isOpen={isModalOpen} closeModal={(): void => setIsModalOpen(false)} images={imagesDisplay} index={0} />
      )}
      <DeleteConfirmation
        isOpen={confirmationBox}
        setOpen={setConfirmationBox}
        itemType="Contents"
        deleteAction={removeContents}
      />
    </>
  );
};
